import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { InboxConversation, MessageStatus, Sender, AttachedFile, Attachment } from '../types';
import { AttachmentIcon, UploadIcon, CheckCircleIcon, DocumentIcon } from './icons';
import { AttachmentPreview } from './AttachmentPreview';

interface InboxPageProps {
    conversation: InboxConversation;
    onReadMore: () => void;
    onSendMessage: (message: string, files: File[]) => void;
}

const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const ChatAttachment = ({ attachment }: { attachment: Attachment }) => {
    const isImage = attachment.type.startsWith('image/');
    return (
        <div className="w-[120px] h-[94px] flex flex-col items-start rounded-lg border border-primary-200 shadow-custom-light relative bg-white">
            <div className="flex-1 self-stretch w-full rounded-t-lg bg-gray-200 flex items-center justify-center overflow-hidden">
                {isImage ? (
                    <img src={attachment.url} alt={attachment.name} className="w-full h-full object-cover" />
                ) : (
                    <DocumentIcon />
                )}
            </div>
            <div className="p-1 self-stretch rounded-b-lg bg-primary-200">
                <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-medium leading-none truncate">{attachment.name}</p>
                <p className="self-stretch text-grayscale-1100 font-grotesk text-[10px] font-light leading-none">{formatBytes(attachment.size)}</p>
            </div>
        </div>
    );
};

const UserMessage: React.FC<{ message: InboxConversation['messages'][0] }> = ({ message }) => {
    const isInitialContact = message.content.includes('Here’s a short profile about us:');

    if (isInitialContact) {
        const parts = message.content.split('\n\n');
        const mainMessage = parts[0] || '';
        const summaryHeader = parts[1] || '';
        const summaryContent = parts.slice(2, -2).join('\n\n');
        const closing = parts.slice(-2).join('\n\n');

        return (
            <div className="p-6 flex-col justify-end items-end gap-4 self-stretch rounded-2xl border border-secondary-300/50 bg-secondary-300/20">
                <div className="flex flex-col items-end self-stretch">
                    <div className="py-2 flex flex-col items-start gap-4 self-stretch">
                         <div className="flex items-center gap-2">
                            <span className="text-grayscale-1300 font-grotesk text-xs">Dear:</span>
                            <span className="text-grayscale-1300 font-grotesk text-sm font-medium">Robert A. Martin</span>
                        </div>
                        <p className="self-stretch text-grayscale-1300 font-grotesk text-sm font-semibold leading-5 whitespace-pre-wrap">{mainMessage}</p>
                        <p className="self-stretch text-grayscale-1300 font-grotesk text-sm leading-5">{summaryHeader}</p>
                        <p className="self-stretch text-grayscale-1300 font-grotesk text-sm leading-5 whitespace-pre-wrap">{summaryContent}</p>
                        <div className="flex flex-col items-start gap-2">
                             <p className="text-grayscale-1300 font-grotesk text-sm leading-5 whitespace-pre-wrap">{closing}</p>
                        </div>
                    </div>
                     <div className="flex justify-end items-center gap-2.5 self-stretch">
                        <span className="text-grayscale-900 font-grotesk text-[10px] italic font-light">{message.timestamp}</span>
                        <div className="flex h-6 py-0 px-1.5 justify-center items-center rounded border-[0.2px] border-secondary-100 bg-secondary-300/10 shadow-custom-light">
                            {message.status === MessageStatus.Read && <CheckCircleIcon />}
                            <span className="text-secondary-500 font-grotesk text-[10px] italic capitalize pl-1">
                                {message.status}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="self-end max-w-[80%]">
            <div className="p-4 flex-col justify-end items-end gap-4 self-stretch rounded-2xl border border-secondary-300/50 bg-secondary-300/20">
                <div className="flex flex-col items-start self-stretch">
                    <p className="self-stretch text-grayscale-1300 font-grotesk text-sm leading-5 whitespace-pre-wrap text-left">{message.content}</p>
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2 justify-end">
                            {message.attachments.map((att, index) => (
                            <ChatAttachment key={index} attachment={att} />
                            ))}
                        </div>
                    )}
                     <div className="flex justify-end items-center gap-2.5 self-stretch mt-2">
                        <span className="text-grayscale-900 font-grotesk text-[10px] italic font-light">{message.timestamp}</span>
                        <div className="flex h-6 py-0 px-1.5 justify-center items-center rounded border-[0.2px] border-secondary-100 bg-secondary-300/10 shadow-custom-light">
                            {message.status === MessageStatus.Read && <CheckCircleIcon />}
                            <span className="text-secondary-500 font-grotesk text-[10px] italic capitalize pl-1">
                                {message.status}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const UniversityMessage: React.FC<{ message: InboxConversation['messages'][0] }> = ({ message }) => {
    // Since we now store plain text in the database, we can display it directly
    const displayContent = message.content || '';

    return (
        <div className="self-start max-w-[80%]">
            <div className="p-4 flex-col justify-end items-start gap-4 self-stretch rounded-2xl border border-grayscale-300 bg-[rgba(240,234,225,0.40)]">
                <div className="py-2 flex flex-col items-start gap-4 self-stretch markdown-content text-grayscale-1300">
                    <div className="whitespace-pre-wrap break-words">
                        {displayContent}
                    </div>
                </div>
                <div className="flex justify-end items-center gap-2.5 self-stretch">
                    <span className="text-grayscale-900 font-grotesk text-[10px] italic font-light">{message.timestamp}</span>
                </div>
            </div>
        </div>
    );
};

export const InboxPage: React.FC<InboxPageProps> = ({ conversation, onReadMore, onSendMessage }) => {
    const [inputValue, setInputValue] = useState('');
    const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [inputValue]);

    // Auto-focus the input when component mounts and maintain focus
    React.useEffect(() => {
        const focusInput = () => {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        };

        // Focus on mount
        focusInput();

        // Handle click events to maintain focus
        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Don't refocus if user clicked on another input, textarea, select, or contenteditable element
            if (target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.tagName === 'SELECT' ||
                target.contentEditable === 'true' ||
                target.closest('input, textarea, select, [contenteditable="true"]')
            )) {
                return;
            }

            // Don't refocus if clicking on buttons, links, or interactive elements
            if (target && (
                target.tagName === 'BUTTON' ||
                target.tagName === 'A' ||
                target.closest('button, a, [role="button"]')
            )) {
                return;
            }

            // Don't refocus if user is interacting with a modal
            if (target && target.closest('[role="dialog"], .fixed.inset-0')) {
                return;
            }

            // Refocus the chat input after a short delay
            setTimeout(() => {
                if (textareaRef.current && document.activeElement !== textareaRef.current) {
                    textareaRef.current.focus();
                }
            }, 50);
        };

        document.addEventListener('click', handleDocumentClick);
        
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);

    // Debug: Log when conversation prop changes
    React.useEffect(() => {
        console.log('InboxPage: Conversation prop updated:', conversation);
        console.log('InboxPage: Messages in conversation:', conversation.messages);
    }, [conversation]);
    
    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
          const newFiles = Array.from(e.target.files);
          const fileWrappers: AttachedFile[] = newFiles.map(file => {
            const id = `${file.name}-${file.lastModified}-${Math.random()}`;
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
              return { file, id, status: 'error', error: 'File size must be under 5MB' };
            }
            
            const wrapper: AttachedFile = { file, id, status: 'success' };
            
            if (file.type.startsWith('image/')) {
              wrapper.status = 'loading';
              const reader = new FileReader();
              reader.onloadend = () => {
                setAttachedFiles(prev => prev.map(f => f.id === id ? { ...f, previewUrl: reader.result as string, status: 'success' } : f));
              };
              reader.readAsDataURL(file);
            } else if (file.type === 'application/pdf') {
              wrapper.status = 'success'; // PDFs don't need preview processing here
            }
            return wrapper;
          });
    
          setAttachedFiles(prev => {
              const updatedFiles = [...prev];
              fileWrappers.forEach(fw => {
                  if(!updatedFiles.find(f => f.file.name === fw.file.name && f.file.lastModified === fw.file.lastModified)) {
                      updatedFiles.push(fw);
                  }
              });
              return updatedFiles;
          });
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
    };
      
    const handleRemoveFile = (idToRemove: string) => {
        setAttachedFiles(prev => prev.filter(f => f.id !== idToRemove));
    };

    const handleSend = () => {
        const filesToSend = attachedFiles.filter(f => f.status === 'success').map(f => f.file);
        if(inputValue.trim()){
            onSendMessage(inputValue, filesToSend);
            setInputValue('');
            setAttachedFiles([]);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if(e.key === 'Enter' && !e.shiftKey){
            e.preventDefault();
            handleSend();
        }
    }

    return (
        <main className="flex-1 flex flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-6 gap-5">
            <div className="p-4 flex-col justify-end items-end gap-4 self-stretch rounded-2xl border border-grayscale-300">
                <div className="flex justify-between items-center self-stretch">
                    <h3 className="text-grayscale-1200 font-grotesk text-base font-bold flex-1 pr-4">{conversation.techCardTitle}</h3>
                    <button onClick={onReadMore} className="flex w-[120px] h-10 py-2.5 px-4 justify-center items-center gap-2 rounded-[32px] border border-primary-500 bg-white">
                        <span className="text-grayscale-1200 text-center font-grotesk text-sm font-medium">Read more</span>
                    </button>
                </div>
            </div>

            <div className='flex-1 overflow-y-auto hide-scrollbar pr-2 flex flex-col gap-4'>
                {conversation.messages.map((message) => (
                    message.sender === Sender.User
                        ? <UserMessage key={message.id} message={message} />
                        : <UniversityMessage key={message.id} message={message} />
                ))}
            </div>

            <div className="mt-auto flex flex-col items-center gap-4 self-stretch pt-6">
                <div className="flex flex-col py-3 px-4 justify-center items-center gap-2 self-stretch rounded-2xl border-[0.5px] border-primary-300 bg-grayscale-100 shadow-custom-light">
                    { attachedFiles.length > 0 && (
                        <div className="flex items-start gap-3 flex-wrap self-stretch p-2">
                            {attachedFiles.map((fileWrapper) => (
                            <AttachmentPreview key={fileWrapper.id} item={fileWrapper} onRemove={() => handleRemoveFile(fileWrapper.id)} />
                            ))}
                        </div>
                    )}
                    <div className="flex w-full items-center gap-4">
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Write a message"
                            className="flex-1 bg-transparent text-grayscale-1100 font-grotesk text-sm focus:outline-none resize-none hide-scrollbar max-h-[120px] overflow-y-auto"
                            rows={1}
                        />
                        <div className="flex items-center gap-2">
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} multiple accept="image/*,application/pdf" />
                            <button className="p-3 rounded-full hover:bg-grayscale-200" onClick={handleAttachmentClick}>
                                <AttachmentIcon />
                            </button>
                            <button
                                onClick={handleSend}
                                className="p-3 rounded-full bg-grayscale-1200 hover:bg-grayscale-1100 disabled:bg-grayscale-600"
                                disabled={!inputValue.trim()}
                            >
                                <UploadIcon />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};